import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Badge } from '@tempo-io/sui-badge';
import { cn } from '@tempo-io/sui-class-utils';
import { TooltipContent } from '@tempo-io/sui-tooltip';
import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { ItemStatusBadge } from '@/components/ui/itemStatusBadge';
import { useGetItemStatusByIdQuery } from '@/lib/queries/common/useGetItemStatusesQuery';
import { useItemTypesByIdQuery } from '@/lib/queries/common/useItemTypesQuery';
import { useGetTeamQuery } from '@/lib/queries/teams/useGetTeamQuery';
import { useGetEstimationsByIds } from '@/lib/queries/work-integrations/useGetEstimations';
import { DateFormat, formatLocalDateWithFallback } from '@/lib/utils/common/dateFormats';
import { STALE_TIME_MS } from './TimelineItemConstants';
import { TimelineItemTooltipDependencies } from './TimelineItemTooltipDependencies';
import type { TimelineItemType, TimelineScenarioContext } from './useTimelineTableColumns';

export const getDuration = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): { weeks: number; days: number } | null => {
  if (!startDate || !endDate) return null;
  const diff = differenceInDays(new Date(endDate), new Date(startDate));
  if (diff < 0) return null;
  const totalDays = diff + 1;
  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 };
};

export const TimelineItemTooltip = ({
  item,
  resizedWidth,
  isTooltipOpen,
  isPublishedMode,
  displayStartDate,
  displayEndDate,
  progress,
  hasProgress,
  mouseX,
  onMouseEnter,
  onMouseLeave,
  scenarioContext,
}: {
  item: TimelineItemType;
  resizedWidth: number;
  isTooltipOpen: boolean;
  isPublishedMode: boolean;
  displayStartDate: string | null | undefined;
  displayEndDate: string | null | undefined;
  progress: number | null;
  hasProgress: boolean;
  mouseX: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  scenarioContext?: TimelineScenarioContext;
}) => {
  const { t } = useTranslation();

  const tooltipAlign = useMemo(() => {
    const thirdWidth = resizedWidth / 3;
    if (mouseX < thirdWidth) return 'start';
    if (mouseX > resizedWidth - thirdWidth) return 'end';
    return 'center';
  }, [mouseX, resizedWidth]);

  const { data: teamData, isLoading: isTeamLoading } = useGetTeamQuery({
    teamId: item.teamId ?? undefined,
    options: {
      staleTime: STALE_TIME_MS,
      enabled: !!item.teamId && isTooltipOpen && !isPublishedMode,
    },
  });
  const { data: estimationsByIds, isLoading: isEstimationSchemeLoading } = useGetEstimationsByIds({
    estimationIds: new Set(item?.estimationId ? [item.estimationId] : []),
    enabled: !!item.estimationId && isTooltipOpen && !isPublishedMode,
    staleTime: STALE_TIME_MS,
  });
  const { data: itemStatuses, isLoading: isItemStatusesLoading } = useGetItemStatusByIdQuery({
    id: item.itemStatusId,
    options: {
      enabled: isTooltipOpen && !!item.itemStatusId && !isPublishedMode,
      staleTime: STALE_TIME_MS,
    },
  });
  const { data: itemTypesById } = useItemTypesByIdQuery({
    enabled: isTooltipOpen && !!item.itemTypeId && !isPublishedMode,
    staleTime: STALE_TIME_MS,
  });
  const itemType = item.itemTypeId ? itemTypesById?.[item.itemTypeId] : undefined;

  const durationLabel = useMemo(() => {
    const duration = getDuration(displayStartDate, displayEndDate);
    if (!duration) return null;
    const { weeks, days } = duration;
    const weeksLabel =
      weeks > 0
        ? t(($) => $.loopVisualization.timelineTooltip.durationWeeks, { count: weeks })
        : '';
    const daysLabel =
      days > 0 ? t(($) => $.loopVisualization.timelineTooltip.durationDays, { count: days }) : '';
    if (weeksLabel && daysLabel) {
      return t(($) => $.loopVisualization.timelineTooltip.durationWeeksDays, {
        weeks: weeksLabel,
        days: daysLabel,
      });
    }
    return weeksLabel || daysLabel || null;
  }, [displayStartDate, displayEndDate, t]);

  return (
    <TooltipContent
      align={tooltipAlign}
      side="bottom"
      className={cn(
        'bg-bg-surface-base text-text-neutral-primary border-2 min-w-64 max-w-100',
        !isPublishedMode && item.dependencies.length > 0 ? 'w-100' : 'w-max',
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDownOutside={(event) => event.preventDefault()}
    >
      <div className="flex flex-col gap-2 p-2 w-full">
        <div className="flex items-center gap-2 mb-2 w-full">
          {!!itemType && <ItemTypeIcon itemType={itemType} className="shrink-0" />}
          <span className="font-bold text-sm flex-1 min-w-0 truncate">{item.name}</span>
          {!!itemType && (
            <Badge variant="secondary" className="shrink-0">
              {itemType.name}
            </Badge>
          )}
        </div>

        <div className="border-b-2 text-text-neutral-secondary" />

        <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 items-center">
          <strong>{t(($) => $.loopVisualization.layout.headers.fields.startDate)}:</strong>
          <span className="text-right">
            {formatLocalDateWithFallback({
              dateString: displayStartDate,
              formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
              fallback: t(($) => $.common.notAvailable),
            })}
          </span>

          <strong>{t(($) => $.loopVisualization.layout.headers.fields.finishDate)}:</strong>
          <span className="text-right">
            {formatLocalDateWithFallback({
              dateString: displayEndDate,
              formatStr: DateFormat.MONTH_SHORT_DAY_SHORT_YEAR,
              fallback: t(($) => $.common.notAvailable),
            })}
          </span>

          {durationLabel !== null && (
            <>
              <strong>{t(($) => $.loopVisualization.timelineTooltip.duration)}:</strong>
              <span className="text-right">{durationLabel}</span>
            </>
          )}

          {hasProgress && (
            <>
              <strong>{t(($) => $.loopVisualization.layout.formatPanel.progressLabel)}:</strong>
              <span className="text-right">{progress}%</span>
            </>
          )}
        </div>

        <div className="border-b-2 text-text-neutral-secondary" />

        <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 items-center">
          {!isEstimationSchemeLoading && estimationsByIds && item.estimationId && (
            <>
              <strong>{t(($) => $.common.filters.labels.items.effortEstimate)}:</strong>
              <span className="flex justify-end">
                <Badge variant="secondary">{estimationsByIds[item.estimationId]?.label}</Badge>
              </span>
            </>
          )}
          {!isTeamLoading && teamData && (
            <>
              <strong>{t(($) => $.loopVisualization.layout.headers.fields.team)}:</strong>
              <span className="flex justify-end">
                <Badge variant="secondary">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={teamData.team.icon} />
                    <AvatarFallback className="bg-bg-neutral">
                      {teamData.team.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  {teamData.team.name}
                </Badge>
              </span>
            </>
          )}
          {!isItemStatusesLoading && itemStatuses && (
            <>
              <strong>{t(($) => $.loopVisualization.layout.headers.fields.status)}:</strong>
              <span className="flex justify-end">
                <ItemStatusBadge label={itemStatuses.name} color={itemStatuses.color} />
              </span>
            </>
          )}
        </div>

        {!isPublishedMode && item.dependencies.length > 0 && (
          <>
            <div className="border-b-2 text-text-neutral-secondary" />
            <TimelineItemTooltipDependencies
              itemId={item.id}
              dependencies={item.dependencies}
              isTooltipOpen={isTooltipOpen}
              scenarioContext={scenarioContext}
            />
          </>
        )}
      </div>
    </TooltipContent>
  );
};
